/**
 * Rating System Utility Functions and Constants
 * Provides core functionality for the rating and review system
 */

// Rating criteria constants
export const RATING_CRITERIA = {
  OVERALL: 'overall',
  FOOD_QUALITY: 'foodQuality',
  DELIVERY_SPEED: 'deliverySpeed',
  CUSTOMER_SERVICE: 'customerService',
  VALUE_FOR_MONEY: 'valueForMoney',
  PACKAGING: 'packaging'
};

// Rating criteria display labels
export const RATING_CRITERIA_LABELS = {
  [RATING_CRITERIA.OVERALL]: 'Overall Experience',
  [RATING_CRITERIA.FOOD_QUALITY]: 'Food Quality',
  [RATING_CRITERIA.DELIVERY_SPEED]: 'Delivery Speed',
  [RATING_CRITERIA.CUSTOMER_SERVICE]: 'Customer Service',
  [RATING_CRITERIA.VALUE_FOR_MONEY]: 'Value for Money',
  [RATING_CRITERIA.PACKAGING]: 'Packaging'
};

// Rating scale constants
export const RATING_SCALE = {
  MIN: 1,
  MAX: 5,
  DEFAULT: 0
};

// Review text limits
export const REVIEW_LIMITS = {
  MAX_LENGTH: 500,
  MIN_LENGTH: 0
};

// Rating validation constants
export const RATING_VALIDATION = {
  REQUIRED_CRITERIA: [RATING_CRITERIA.OVERALL],
  EDIT_WINDOW_DAYS: 7
};

// Report reasons
export const REPORT_REASONS = {
  INAPPROPRIATE: 'inappropriate',
  SPAM: 'spam',
  FAKE: 'fake',
  OFFENSIVE: 'offensive',
  OTHER: 'other'
};

// Report reason labels
export const REPORT_REASON_LABELS = {
  [REPORT_REASONS.INAPPROPRIATE]: 'Inappropriate Content',
  [REPORT_REASONS.SPAM]: 'Spam',
  [REPORT_REASONS.FAKE]: 'Fake Review',
  [REPORT_REASONS.OFFENSIVE]: 'Offensive Language',
  [REPORT_REASONS.OTHER]: 'Other'
};

// Moderation actions
export const MODERATION_ACTIONS = {
  HIDE: 'hide',
  SHOW: 'show',
  DISMISS: 'dismiss'
};

/**
 * Validates a rating value
 * @param {number} rating - The rating value to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidRating = (rating) => {
  return typeof rating === 'number' && 
         rating >= RATING_SCALE.MIN && 
         rating <= RATING_SCALE.MAX;
};

/**
 * Validates a complete rating object
 * @param {Object} ratings - The ratings object to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateRatings = (ratings) => {
  const errors = [];
  
  // Check if overall rating is provided (required)
  if (!ratings[RATING_CRITERIA.OVERALL] || !isValidRating(ratings[RATING_CRITERIA.OVERALL])) {
    errors.push('Overall rating is required and must be between 1-5');
  }
  
  // Validate all provided ratings
  Object.keys(ratings).forEach(criteria => {
    if (ratings[criteria] && !isValidRating(ratings[criteria])) {
      errors.push(`${RATING_CRITERIA_LABELS[criteria] || criteria} must be between 1-5`);
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates review text
 * @param {string} reviewText - The review text to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateReviewText = (reviewText) => {
  const errors = [];
  
  if (reviewText && reviewText.length > REVIEW_LIMITS.MAX_LENGTH) {
    errors.push(`Review text cannot exceed ${REVIEW_LIMITS.MAX_LENGTH} characters`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Calculates the average rating from a ratings object
 * @param {Object} ratings - The ratings object
 * @returns {number} The average rating
 */
export const calculateAverageRating = (ratings) => {
  const validRatings = Object.values(ratings).filter(rating => 
    typeof rating === 'number' && rating > 0
  );
  
  if (validRatings.length === 0) return 0;
  
  const sum = validRatings.reduce((acc, rating) => acc + rating, 0);
  return Math.round((sum / validRatings.length) * 10) / 10; // Round to 1 decimal
};

/**
 * Formats a rating for display
 * @param {number} rating - The rating to format
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted rating string
 */
export const formatRating = (rating, decimals = 1) => {
  if (!rating || rating === 0) return '0.0';
  return Number(rating).toFixed(decimals);
};

/**
 * Generates star display array for a rating
 * @param {number} rating - The rating value
 * @returns {Array} Array of star states ('full', 'half', 'empty')
 */
export const generateStarArray = (rating) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  
  // Add full stars
  for (let i = 0; i < fullStars; i++) {
    stars.push('full');
  }
  
  // Add half star if needed
  if (hasHalfStar && fullStars < RATING_SCALE.MAX) {
    stars.push('half');
  }
  
  // Fill remaining with empty stars
  while (stars.length < RATING_SCALE.MAX) {
    stars.push('empty');
  }
  
  return stars;
};

/**
 * Checks if a rating can be edited (within edit window)
 * @param {string} createdAt - The rating creation date
 * @returns {boolean} True if editable, false otherwise
 */
export const canEditRating = (createdAt) => {
  const createdDate = new Date(createdAt);
  const now = new Date();
  const daysDiff = (now - createdDate) / (1000 * 60 * 60 * 24);
  
  return daysDiff <= RATING_VALIDATION.EDIT_WINDOW_DAYS;
};

/**
 * Formats a date for rating display
 * @param {string} dateString - The date string to format
 * @returns {string} Formatted date string
 */
export const formatRatingDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = Math.abs(now - date) / (1000 * 60 * 60);
  
  if (diffInHours < 1) {
    return 'Just now';
  } else if (diffInHours < 24) {
    const hours = Math.floor(diffInHours);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (diffInHours < 48) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  }
};

/**
 * Gets the display color class for a rating value
 * @param {number} rating - The rating value
 * @returns {string} CSS color class
 */
export const getRatingColorClass = (rating) => {
  if (rating >= 4.5) return 'text-green-600';
  if (rating >= 4.0) return 'text-green-500';
  if (rating >= 3.5) return 'text-yellow-500';
  if (rating >= 3.0) return 'text-orange-500';
  if (rating >= 2.0) return 'text-red-500';
  return 'text-red-600';
};

/**
 * Gets the background color class for a rating badge
 * @param {number} rating - The rating value
 * @returns {string} CSS background color class
 */
export const getRatingBadgeClass = (rating) => {
  if (rating >= 4.5) return 'bg-green-100 text-green-800 border-green-200';
  if (rating >= 4.0) return 'bg-green-50 text-green-700 border-green-200';
  if (rating >= 3.5) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  if (rating >= 3.0) return 'bg-orange-100 text-orange-800 border-orange-200';
  if (rating >= 2.0) return 'bg-red-100 text-red-800 border-red-200';
  return 'bg-red-200 text-red-900 border-red-300';
};

/**
 * Creates an empty rating object with default values
 * @returns {Object} Empty rating object
 */
export const createEmptyRating = () => {
  return {
    [RATING_CRITERIA.OVERALL]: RATING_SCALE.DEFAULT,
    [RATING_CRITERIA.FOOD_QUALITY]: RATING_SCALE.DEFAULT,
    [RATING_CRITERIA.DELIVERY_SPEED]: RATING_SCALE.DEFAULT,
    [RATING_CRITERIA.CUSTOMER_SERVICE]: RATING_SCALE.DEFAULT,
    [RATING_CRITERIA.VALUE_FOR_MONEY]: RATING_SCALE.DEFAULT,
    [RATING_CRITERIA.PACKAGING]: RATING_SCALE.DEFAULT
  };
};

/**
 * Filters ratings based on criteria
 * @param {Array} ratings - Array of rating objects
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered ratings array
 */
export const filterRatings = (ratings, filters = {}) => {
  let filtered = [...ratings];
  
  // Filter by minimum rating
  if (filters.minRating) {
    filtered = filtered.filter(rating => 
      rating.ratings?.overall >= filters.minRating
    );
  }
  
  // Filter by verified purchases only
  if (filters.verifiedOnly) {
    filtered = filtered.filter(rating => rating.isVerifiedPurchase);
  }
  
  // Filter by date range
  if (filters.startDate) {
    const startDate = new Date(filters.startDate);
    filtered = filtered.filter(rating => 
      new Date(rating.createdAt) >= startDate
    );
  }
  
  if (filters.endDate) {
    const endDate = new Date(filters.endDate);
    filtered = filtered.filter(rating => 
      new Date(rating.createdAt) <= endDate
    );
  }
  
  return filtered;
};

/**
 * Sorts ratings based on criteria
 * @param {Array} ratings - Array of rating objects
 * @param {string} sortBy - Sort criteria ('date', 'rating', 'helpful')
 * @param {string} order - Sort order ('asc', 'desc')
 * @returns {Array} Sorted ratings array
 */
export const sortRatings = (ratings, sortBy = 'date', order = 'desc') => {
  const sorted = [...ratings];
  
  sorted.sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'rating':
        aValue = a.ratings?.overall || 0;
        bValue = b.ratings?.overall || 0;
        break;
      case 'helpful':
        aValue = a.helpfulVotes || 0;
        bValue = b.helpfulVotes || 0;
        break;
      case 'date':
      default:
        aValue = new Date(a.createdAt);
        bValue = new Date(b.createdAt);
        break;
    }
    
    if (order === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });
  
  return sorted;
};

/**
 * Calculates rating statistics from an array of ratings
 * @param {Array} ratings - Array of rating objects
 * @returns {Object} Rating statistics
 */
export const calculateRatingStats = (ratings) => {
  if (!ratings || ratings.length === 0) {
    return {
      totalRatings: 0,
      averageRating: 0,
      distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      criteriaAverages: createEmptyRating(),
      verifiedPurchaseRate: 0,
      reviewRate: 0
    };
  }
  
  const stats = {
    totalRatings: ratings.length,
    distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
    criteriaAverages: createEmptyRating(),
    verifiedPurchaseRate: 0,
    reviewRate: 0
  };
  
  // Calculate distribution and averages
  const criteriaSums = createEmptyRating();
  let verifiedCount = 0;
  let reviewCount = 0;
  
  ratings.forEach(rating => {
    const overallRating = Math.floor(rating.overallRating || 0);
    if (overallRating >= 1 && overallRating <= 5) {
      stats.distribution[overallRating]++;
    }
    
    // Sum criteria ratings
    Object.keys(RATING_CRITERIA).forEach(key => {
      const criteria = RATING_CRITERIA[key];
      if (rating[criteria]) {
        criteriaSums[criteria] += rating[criteria];
      }
    });
    
    // Count verified purchases and reviews
    if (rating.isVerifiedPurchase) verifiedCount++;
    if (rating.review && rating.review.trim()) reviewCount++;
  });
  
  // Calculate averages
  Object.keys(criteriaSums).forEach(criteria => {
    stats.criteriaAverages[criteria] = 
      Math.round((criteriaSums[criteria] / ratings.length) * 10) / 10;
  });
  
  stats.averageRating = stats.criteriaAverages[RATING_CRITERIA.OVERALL];
  stats.verifiedPurchaseRate = Math.round((verifiedCount / ratings.length) * 100);
  stats.reviewRate = Math.round((reviewCount / ratings.length) * 100);
  
  return stats;
};

/**
 * Error handling for rating operations
 * @param {Error} error - The error object
 * @returns {string} User-friendly error message
 */
export const handleRatingError = (error) => {
  if (error.response) {
    const { status, data } = error.response;
    switch (status) {
      case 400:
        return data.message || 'Invalid rating data provided';
      case 401:
        return 'Please log in to rate restaurants';
      case 403:
        return 'You are not authorized to perform this action';
      case 404:
        return 'Restaurant or order not found';
      case 409:
        return 'You have already rated this restaurant';
      case 429:
        return 'Too many requests. Please try again later';
      default:
        return data.message || 'Failed to process rating';
    }
  }
  
  if (error.request) {
    return 'Network error. Please check your connection';
  }
  
  return error.message || 'An unexpected error occurred';
};